/**
 * Future Cartridge battle status panel — Phaser-wired DOM glass.
 *
 * One per Pokémon (opponent top-right, player above-log). Composes:
 *   - Name + level row
 *   - HP bar (mounted inline via battleHpBar.ts) + HP numeric (player)
 *   - EXP bar (player only)
 *
 * Replaces three m5x7 BitmapText fields (opponentLabel, playerLabel,
 * playerExpText) plus the standalone HP bar's corner anchoring. The
 * status panel is the new corner anchor; the HP bar sits inside it.
 *
 * Handle proxies HP / type / sweep / flash / shake to the inner HP
 * bar so existing BattleScene call sites that used the HP bar handle
 * don't need to change beyond the field rename.
 *
 * Anchoring rationale:
 *  - Opponent: top-right, matches the existing HP bar location.
 *  - Player: above the log (log bottom 304 + height ~87 = top 391
 *    from canvas bottom; panel sits 12 px above). Bottom-left corner
 *    is unusable — the action grid (BACK + 2×2) occupies the bottom
 *    chrome stack and a 168 px wide panel anchored there would
 *    overlap the grid bottom-left button on viewports ≤ ~480 px.
 */
import Phaser from 'phaser';
import {
  mountBattleHpBar,
  type BattleHpBarHandle,
} from './battleHpBar';
import { Z_DIALOGUE } from './zLayers';
import { expForLevel } from '../data/expCurve';
import { BATTLE_LOG_TOP_ESTIMATE_PX } from './battleLog';
import type { BannerBox, BannerPlacement } from './duelBannerLayout';

const PARENT_ID = 'game';

export type BattleStatusPanelSide = 'opponent' | 'player';

export interface BattleStatusPanelHandle {
  setName(name: string): void;
  setLevel(level: number): void;
  setHp(hpPct: number, snap?: boolean): void;
  /** No-op on opponent side (only player shows the numeric). */
  setHpNumeric(current: number, max: number): void;
  setType(type: string): void;
  /** Compute within-level progress for the EXP bar fill from raw
   *  cumulative exp + current level. No-op on opponent side. */
  setExp(currentExp: number, level: number): void;
  triggerSweep(speed: 'slow' | 'normal' | 'fast'): void;
  triggerFlash(): void;
  triggerShake(): void;
  /** Mark the inner HP bar as fainted — drives the 3× red pulse +
   *  type-tint drain. Sticky for the rest of the battle. */
  triggerFaint(): void;
  /** Mark/unmark this Pokémon as the player's last alive partner.
   *  Combined with critical (< 10 % HP) drives the last-alive
   *  cinematic pulse + canvas dim. */
  setLastAlive(value: boolean): void;
  /** FIELD ANCHOR ONLY (in-world duels): move the banner to a screen
   *  rect and point its stem at its creature. No-op in corner mode. */
  setFieldPlacement(p: BannerPlacement): void;
  /** FIELD ANCHOR ONLY: the banner's rendered size, for the layout
   *  math. Measured live so a long name or a status chip is accounted
   *  for rather than assumed. */
  measure(): BannerBox;
  destroy(): void;
}

export function mountBattleStatusPanel(
  scene: Phaser.Scene,
  opts: {
    side: BattleStatusPanelSide;
    /** 'corner' (default) = the classic battle screen's fixed corners.
     *  'field' = a Ringside Banner that rides its creature in an
     *  in-world duel (owner ruling 2026-08-03). Additive: every
     *  existing call site keeps the corner behavior untouched. */
    anchor?: 'corner' | 'field';
  },
): BattleStatusPanelHandle {
  const parent = document.getElementById(PARENT_ID) ?? document.body;
  const field = opts.anchor === 'field';

  const root = document.createElement('div');
  root.className = 'battle-status-panel';
  root.dataset.side = opts.side;
  if (field) root.dataset.anchor = 'field';
  root.style.zIndex = String(Z_DIALOGUE - 1);
  if (field) {
    // Positioned every layout pass by setFieldPlacement; start
    // off-screen so a pre-layout paint can't flash in the corner.
    root.style.left = '-9999px';
    root.style.top = '0px';
  } else if (opts.side === 'opponent') {
    root.style.right = '24px';
    root.style.top = '16px';
  } else {
    // Derived, not hand-synced: 12 px above the log's top edge.
    root.style.bottom = `${BATTLE_LOG_TOP_ESTIMATE_PX + 12}px`;
    root.style.left = '24px';
  }

  // Name + level row
  const nameRow = document.createElement('div');
  nameRow.className = 'battle-status-panel__name-row';
  const nameEl = document.createElement('span');
  nameEl.className = 'battle-status-panel__name';
  const levelEl = document.createElement('span');
  levelEl.className = 'battle-status-panel__level';
  nameRow.append(nameEl, levelEl);

  // HP row — bar (inline-mounted below) + numeric
  const hpRow = document.createElement('div');
  hpRow.className = 'battle-status-panel__hp-row';
  const hpNumericEl = document.createElement('span');
  hpNumericEl.className = 'battle-status-panel__hp-numeric';
  if (opts.side === 'opponent') hpNumericEl.style.display = 'none';

  root.append(nameRow, hpRow);

  // Field anchor: the stem that points at this banner's own creature.
  // It is the entire ownership signal — no leader lines, no color
  // coding, just "this plate belongs to that animal".
  let stemEl: HTMLDivElement | undefined;
  if (field) {
    stemEl = document.createElement('div');
    stemEl.className = 'battle-status-panel__stem';
    root.appendChild(stemEl);
  }

  // EXP bar (player only)
  let expFillEl: HTMLDivElement | undefined;
  if (opts.side === 'player') {
    const expBar = document.createElement('div');
    expBar.className = 'battle-status-panel__exp-bar';
    expFillEl = document.createElement('div');
    expFillEl.className = 'battle-status-panel__exp-fill';
    expBar.appendChild(expFillEl);
    root.appendChild(expBar);
  }

  parent.appendChild(root);

  // Inline-mount the HP bar into the hp row; flex layout positions
  // it. Append the numeric AFTER mounting so DOM order is
  // [hp-bar, hp-numeric] left-to-right.
  const hpBarHandle: BattleHpBarHandle = mountBattleHpBar(scene, {
    position: opts.side === 'opponent' ? 'top-right' : 'bottom-left',
    inline: true,
    parent: hpRow,
    // The banner is sized to its content, so a fixed 80 px bar would
    // leave dead space beside it at every width. Corner panels keep
    // the fixed bar — that layout is shipped and unrelated.
    fluidWidth: field,
  });
  hpRow.appendChild(hpNumericEl);

  const onSleep = (): void => {
    root.style.display = 'none';
  };
  const onWake = (): void => {
    root.style.display = '';
  };
  scene.events.on(Phaser.Scenes.Events.SLEEP, onSleep);
  scene.events.on(Phaser.Scenes.Events.WAKE, onWake);

  return {
    setName(name): void {
      nameEl.textContent = name;
    },
    setLevel(level): void {
      levelEl.textContent = `Lv ${level}`;
    },
    setHp(hpPct, snap): void {
      hpBarHandle.setHp(hpPct, snap);
    },
    setHpNumeric(current, max): void {
      if (opts.side === 'opponent') return;
      hpNumericEl.textContent = `${current} / ${max}`;
    },
    setType(type): void {
      hpBarHandle.setType(type);
    },
    setExp(currentExp, level): void {
      if (!expFillEl) return;
      const lo = expForLevel(level);
      const hi = expForLevel(level + 1);
      const range = hi - lo;
      const progress =
        range > 0 ? Math.max(0, Math.min(1, (currentExp - lo) / range)) : 0;
      expFillEl.style.width = `${progress * 100}%`;
    },
    triggerSweep: hpBarHandle.triggerSweep,
    triggerFlash: hpBarHandle.triggerFlash,
    triggerShake: hpBarHandle.triggerShake,
    triggerFaint: hpBarHandle.triggerFaint,
    setLastAlive: hpBarHandle.setLastAlive,
    setFieldPlacement(p): void {
      if (!field) return;
      root.style.left = `${p.left}px`;
      root.style.top = `${p.top}px`;
      root.style.bottom = '';
      root.style.right = '';
      root.dataset.stem = p.stemSide;
      if (stemEl) stemEl.style.left = `${p.stemX}px`;
    },
    measure(): BannerBox {
      return { w: root.offsetWidth, h: root.offsetHeight };
    },
    destroy(): void {
      scene.events.off(Phaser.Scenes.Events.SLEEP, onSleep);
      scene.events.off(Phaser.Scenes.Events.WAKE, onWake);
      hpBarHandle.destroy();
      root.remove();
    },
  };
}
